import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useSportsData } from "../contexts/SportsDataContext";
import { useVideoPlayer } from "../contexts/VideoPlayerContext";
import GameCard from "../components/GameCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Play, Trophy, Users, Newspaper, Lightning } from "@phosphor-icons/react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeagueHub() {
  const { leagueKey } = useParams();
  const { fetchLeagueScores } = useSportsData();
  const { playVideo } = useVideoPlayer();
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [standings, setStandings] = useState([]);
  const [news, setNews] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leagueName, setLeagueName] = useState("");

  useEffect(() => {
    if (!leagueKey) return;
    setLoading(true);
    Promise.all([
      fetchLeagueScores(leagueKey).then(setGames),
      axios.get(`${API}/teams/${leagueKey}`).then((r) => {
        setTeams(r.data.teams || []);
        setLeagueName(r.data.leagueName || leagueKey.toUpperCase());
      }).catch(() => {}),
      axios.get(`${API}/standings/${leagueKey}`).then((r) => setStandings(r.data.groups || [])).catch(() => {}),
      axios.get(`${API}/news/${leagueKey}`).then((r) => setNews(r.data.articles || [])).catch(() => {}),
      axios.get(`${API}/highlights`, { params: { sport: leagueKey } }).then((r) => setHighlights(r.data.highlights || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [leagueKey, fetchLeagueScores]);

  const sortedGames = [...games].sort((a, b) => {
    const order = { in: 0, pre: 1, post: 2 };
    return (order[a.statusState] ?? 3) - (order[b.statusState] ?? 3);
  });

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="league-hub">
      {/* League header */}
      <div className="flex items-center gap-3">
        <Trophy size={28} weight="fill" className="text-[#007AFF]" />
        <h1 className="font-['Bebas_Neue'] text-4xl sm:text-5xl tracking-tighter uppercase text-white" data-testid="league-title">
          {leagueName || leagueKey?.toUpperCase()}
        </h1>
      </div>

      <Tabs defaultValue="scores" className="w-full">
        <TabsList className="bg-[#141414] border border-white/10 p-1 rounded-lg">
          <TabsTrigger value="scores" className="text-xs tracking-wider uppercase data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-scores">
            Scores
          </TabsTrigger>
          <TabsTrigger value="standings" className="text-xs tracking-wider uppercase data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-standings">
            Standings
          </TabsTrigger>
          <TabsTrigger value="teams" className="text-xs tracking-wider uppercase data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-teams">
            Teams
          </TabsTrigger>
          <TabsTrigger value="news" className="text-xs tracking-wider uppercase data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-news">
            News
          </TabsTrigger>
        </TabsList>

        {/* Scores */}
        <TabsContent value="scores" className="mt-4">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(4)].map((_, i) => <div key={i} className="bg-[#141414] border border-white/10 rounded-lg h-[140px] animate-pulse" />)}
            </div>
          ) : sortedGames.length === 0 ? (
            <p className="text-center py-12 text-white/30 text-sm">No games scheduled</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children" data-testid="league-games-grid">
              {sortedGames.map((g) => <GameCard key={g.id} game={g} />)}
            </div>
          )}
          {/* League highlights */}
          {highlights.length > 0 && (
            <div className="mt-6 space-y-3">
              <h3 className="font-['Bebas_Neue'] text-xl uppercase text-white flex items-center gap-2">
                <Lightning size={18} weight="fill" className="text-[#007AFF]" /> Highlights
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {highlights.map((h) => (
                  <button key={h.id} onClick={() => playVideo(h, highlights)} className="group bg-[#141414] border border-white/10 rounded-lg overflow-hidden text-left hover:border-white/20 transition-colors" data-testid={`league-highlight-${h.id}`}>
                    <div className="relative aspect-video bg-black/50">
                      <img src={`https://img.youtube.com/vi/${h.youtubeId}/mqdefault.jpg`} alt={h.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" loading="lazy" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-8 h-8 rounded-full bg-[#007AFF]/90 flex items-center justify-center"><Play size={14} weight="fill" className="text-white ml-0.5" /></div>
                      </div>
                    </div>
                    <p className="px-2 py-1.5 text-xs text-white/80 truncate">{h.title}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Standings */}
        <TabsContent value="standings" className="mt-4 space-y-4">
          {standings.length === 0 ? (
            <p className="text-center py-12 text-white/30 text-sm">No standings available</p>
          ) : standings.map((group) => (
            <div key={group.name} className="bg-[#141414] border border-white/10 rounded-lg overflow-hidden" data-testid={`standings-group-${group.name}`}>
              <div className="px-4 py-2 border-b border-white/5">
                <h3 className="text-xs tracking-widest uppercase font-bold text-white/60">{group.name}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-white/40 uppercase tracking-wider border-b border-white/5">
                      <th className="text-left px-4 py-2 w-8">#</th>
                      <th className="text-left px-2 py-2">Team</th>
                      <th className="text-center px-2 py-2">W</th>
                      <th className="text-center px-2 py-2">L</th>
                      <th className="text-center px-2 py-2">PCT</th>
                      <th className="text-center px-2 py-2">STRK</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.entries.map((entry, idx) => (
                      <tr key={entry.teamId} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="px-4 py-2 text-white/30">{idx + 1}</td>
                        <td className="px-2 py-2">
                          <Link to={`/team/${leagueKey}/${entry.teamId}`} className="flex items-center gap-2 hover:text-[#007AFF] transition-colors" data-testid={`standings-team-${entry.teamId}`}>
                            {entry.teamLogo && <img src={entry.teamLogo} alt="" className="w-5 h-5 object-contain" />}
                            <span className="text-white/90">{entry.teamAbbr || entry.teamName}</span>
                          </Link>
                        </td>
                        <td className="text-center px-2 py-2 text-white/70 score-font">{entry.wins}</td>
                        <td className="text-center px-2 py-2 text-white/70 score-font">{entry.losses}</td>
                        <td className="text-center px-2 py-2 text-white/70 score-font">{entry.winPercent}</td>
                        <td className="text-center px-2 py-2 text-white/50">{entry.streak}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </TabsContent>

        {/* Teams */}
        <TabsContent value="teams" className="mt-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="teams-grid">
            {teams.map((t) => (
              <Link
                key={t.id}
                to={`/team/${leagueKey}/${t.id}`}
                className="bg-[#141414] border border-white/10 rounded-lg p-4 flex flex-col items-center gap-2 hover:border-white/20 transition-colors text-center"
                data-testid={`team-card-${t.id}`}
              >
                {t.logo ? (
                  <img src={t.logo} alt={t.name} className="w-12 h-12 object-contain" loading="lazy" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold" style={t.color ? {backgroundColor: `#${t.color}20`, color: `#${t.color}`} : {}}>
                    {t.abbreviation?.slice(0,2)}
                  </div>
                )}
                <p className="text-xs text-white/80 font-medium">{t.displayName || t.name}</p>
                <p className="text-[10px] text-white/30">{t.abbreviation}</p>
              </Link>
            ))}
          </div>
        </TabsContent>

        {/* News */}
        <TabsContent value="news" className="mt-4 space-y-3">
          {news.length === 0 ? (
            <p className="text-center py-12 text-white/30 text-sm">No news available</p>
          ) : news.map((article, idx) => (
            <a
              key={idx}
              href={article.link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex gap-4 bg-[#141414] border border-white/10 rounded-lg p-3 hover:border-white/20 transition-colors"
              data-testid={`news-article-${idx}`}
            >
              {article.image && (
                <img src={article.image} alt="" className="w-24 h-16 object-cover rounded flex-shrink-0" loading="lazy" />
              )}
              <div className="min-w-0">
                <h4 className="text-sm text-white font-semibold line-clamp-2">{article.headline}</h4>
                <p className="text-xs text-white/40 mt-1 line-clamp-1">{article.description}</p>
              </div>
            </a>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
